import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Brain, TrendingUp, AlertTriangle, Check, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BrainPage() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReport = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/brain-coach/report`, { credentials: 'include' });
      if (res.ok) setReport(await res.json());
    } catch (err) { logger.error("Operation failed:", err); } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const sortedToneProfile = useMemo(() => {
    if (!report?.tone_distribution) return [];
    return Object.entries(report.tone_distribution).sort(([, a], [, b]) => b - a);
  }, [report?.tone_distribution]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
      </div>
    );
  }

  const brainScore = report?.brain_score || 0;
  const traits = report?.traits || [];
  const insights = report?.insights || [];
  const stats = report?.stats || {};

  return (
    <div className="space-y-8" data-testid="brain-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Your Communication Brain</h1>
        <p className="text-white/55">AI coaching based on your actual usage data</p>
      </div>

      {/* Brain Score Card */}
      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-2 border-amber-500/30 rounded-2xl p-6 md:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div className="flex items-center gap-6">
            <div className="relative">
              <svg className="w-24 h-24 -rotate-90">
                <circle cx="48" cy="48" r="40" stroke="rgba(255,255,255,0.1)" strokeWidth="8" fill="none" />
                <circle
                  cx="48" cy="48" r="40"
                  stroke="url(#brainGradient)" strokeWidth="8" fill="none"
                  strokeDasharray={`${brainScore * 2.51} 251`}
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="brainGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F2C94C" />
                    <stop offset="100%" stopColor="#F59E0B" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-white font-space text-2xl" data-testid="brain-score">{brainScore}</span>
              </div>
            </div>
            <div>
              <h2 className="text-white font-medium text-lg">Communication Score</h2>
              {stats.week_checks > 0 ? (
                <p className="text-emerald-400 text-sm flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {stats.week_checks} checks this week
                </p>
              ) : (
                <p className="text-white/40 text-sm">Use SendPI to build your score</p>
              )}
              {stats.improvement > 0 && (
                <p className="text-white/55 text-xs mt-1">+{stats.improvement} avg PI improvement</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="bg-black/20 rounded-xl p-3">
              <p className="text-white font-space text-xl">{stats.total_checks || 0}</p>
              <p className="text-white/40 text-xs">Total Checks</p>
            </div>
            <div className="bg-black/20 rounded-xl p-3">
              <p className="text-white font-space text-xl">{stats.acceptance_rate || 0}%</p>
              <p className="text-white/40 text-xs">Accept Rate</p>
            </div>
            <div className="bg-black/20 rounded-xl p-3">
              <p className="text-white font-space text-xl">{stats.tones_used || 0}</p>
              <p className="text-white/40 text-xs">Tones Used</p>
            </div>
            <div className="bg-black/20 rounded-xl p-3">
              <p className="text-white font-space text-xl">{stats.days_active || 0}</p>
              <p className="text-white/40 text-xs">Days Active</p>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Insights */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-6">Coaching Insights</h3>
        <div className="space-y-4">
          {insights.map((insight, i) => (
            <div
              key={`${insight.type}-${insight.text.slice(0, 30)}`}
              className={`p-4 rounded-xl border ${
                insight.type === 'warning'
                  ? 'bg-amber-500/5 border-amber-500/20'
                  : insight.type === 'success'
                  ? 'bg-emerald-500/5 border-emerald-500/20'
                  : 'bg-cyan-500/5 border-cyan-500/20'
              }`}
              data-testid={`insight-${i}`}
            >
              <div className="flex items-start gap-3">
                {insight.type === 'warning' && <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />}
                {insight.type === 'success' && <Check className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />}
                {insight.type === 'focus' && <ArrowRight className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />}
                <div>
                  <span className={`text-xs uppercase tracking-wider ${
                    insight.type === 'warning' ? 'text-amber-400' :
                    insight.type === 'success' ? 'text-emerald-400' : 'text-cyan-400'
                  }`}>
                    {insight.type === 'warning' ? 'Pattern Detected' :
                     insight.type === 'success' ? 'Strength' : "Focus Area"}
                  </span>
                  <p className="text-white/70 text-sm mt-1">{insight.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Trait Breakdown */}
      <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
        <h3 className="text-white font-medium mb-6">Communication Traits</h3>
        <div className="space-y-6">
          {traits.map((trait) => (
            <div key={trait.name}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-white/70 text-sm">{trait.name}</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{trait.score}%</span>
                  <span className={`text-xs ${trait.change.startsWith('+') && parseInt(trait.change) > 0 ? 'text-emerald-400' : 'text-white/40'}`}>
                    {trait.change}
                  </span>
                </div>
              </div>
              <Progress value={trait.score} className="h-2 bg-white/10" />
            </div>
          ))}
        </div>
      </div>

      {/* Tone Distribution */}
      {sortedToneProfile.length > 0 && (
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          <h3 className="text-white font-medium mb-6">Your Tone Profile</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sortedToneProfile.map(([tone, count]) => (
                <div key={tone} className="bg-white/5 rounded-xl p-3 text-center">
                  <p className="text-white font-medium capitalize">{tone}</p>
                  <p className="text-white/40 text-sm">{count} uses</p>
                </div>
              ))
            }
          </div>
        </div>
      )}

      {/* CTA to use more */}
      {stats.total_checks < 10 && (
        <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/20 rounded-2xl p-6 text-center">
          <Brain className="w-10 h-10 text-cyan-400 mx-auto mb-3" />
          <h3 className="text-white font-medium mb-2">Build Your Communication Profile</h3>
          <p className="text-white/50 text-sm max-w-md mx-auto mb-4">
            Use SendPI's PI Check and Rewrite features more to unlock deeper coaching insights.
            The more you use it, the smarter your Brain Coach becomes.
          </p>
          <Button variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 rounded-full">
            Learn how to get started
          </Button>
        </div>
      )}
    </div>
  );
}
